import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Typography, Box, Alert, IconButton, CircularProgress, Button } from '@mui/material';
import { Close as CloseIcon } from '../icons';
import { Refresh as RefreshIcon } from '../icons';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import { T } from './Translate';

const INVALID_QR_MESSAGE_MS = 2500;

// Pantalla propia (no popup): se monta/desmonta con la ruta /QRScanner, así
// que la cámara arranca al entrar y se libera sola al salir (cambio de ruta
// = unmount = corre el cleanup de este mismo efecto).
const QRScanner = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraError, setCameraError] = useState(null);
  const [invalidQrMessage, setInvalidQrMessage] = useState(false);
  const [isStarting, setIsStarting] = useState(true);
  const [retryKey, setRetryKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let stream = null;
    let animationFrame = null;
    let invalidQrTimeout = null;
    const videoEl = videoRef.current;

    const startCamera = async () => {
      setIsStarting(true);
      setCameraError(null);
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setIsStarting(false);
          const scanQRCode = () => {
            if (videoRef.current?.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
              const canvas = canvasRef.current;
              const video = videoRef.current;
              canvas.height = video.videoHeight;
              canvas.width = video.videoWidth;
              const context = canvas.getContext('2d');
              context.drawImage(video, 0, 0, canvas.width, canvas.height);
              const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
              const code = jsQR(imageData.data, imageData.width, imageData.height);
              if (code) {
                try {
                  const qrData = JSON.parse(code.data);
                  if (stream) {
                    stream.getTracks().forEach(track => track.stop());
                  }
                  navigate('/QRScannerPay', {
                    state: {
                      couponData: {
                        ...qrData,
                        scannedAt: new Date().toISOString()
                      }
                    }
                  });
                  return;
                } catch (err) {
                  // Código leído pero no es el formato esperado: se avisa
                  // sin frenar el escaneo, por si la cámara está apuntando
                  // a otra cosa (ej. un QR ajeno) de paso.
                  setInvalidQrMessage(true);
                  if (invalidQrTimeout) clearTimeout(invalidQrTimeout);
                  invalidQrTimeout = setTimeout(() => setInvalidQrMessage(false), INVALID_QR_MESSAGE_MS);
                }
              }
            }
            animationFrame = requestAnimationFrame(scanQRCode);
          };
          scanQRCode();
        }
      } catch (err) {
        setIsStarting(false);
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setCameraError(<T>No diste permiso para usar la cámara. Habilitalo desde la configuración del navegador e intentá de nuevo.</T>);
        } else if (err.name === 'NotFoundError' || err.name === 'OverconstrainedError') {
          setCameraError(<T>No encontramos ninguna cámara disponible en este dispositivo.</T>);
        } else {
          setCameraError(<T>No se pudo acceder a la cámara.</T>);
        }
      }
    };
    startCamera();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      if (invalidQrTimeout) {
        clearTimeout(invalidQrTimeout);
      }
      if (videoEl) {
        videoEl.srcObject = null;
      }
    };
  }, [navigate, retryKey]);

  const handleRetry = useCallback(() => {
    setRetryKey((key) => key + 1);
  }, []);

  return (
    <Box id="tour-qrscanner" sx={{ position: 'fixed', zIndex: 1300, top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(24,26,27,0.98)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', top: 16, right: 16, color: 'white', zIndex: 1400 }}>
        <CloseIcon />
      </IconButton>
      <Box sx={{ width: '100%', maxWidth: 400, mb: 2 }}>
        <Typography variant="h5" align="center" color="white" gutterBottom>
          <T>Escanear QR</T>
        </Typography>
        {!cameraError && (
          <Typography variant="body2" align="center" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <T>Apuntá la cámara al código QR de la oferta</T>
          </Typography>
        )}
      </Box>

      {cameraError ? (
        <Box sx={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Alert severity="error" sx={{ width: '100%' }}>
            {cameraError}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRetry}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
          >
            <T>Reintentar</T>
          </Button>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', width: '100%', maxWidth: 400, aspectRatio: '1', mb: 2 }}>
          <video
            ref={videoRef}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 12,
              background: '#000'
            }}
            autoPlay
            playsInline
            muted
          />
          <canvas
            ref={canvasRef}
            style={{ display: 'none' }}
          />
          {isStarting && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                borderRadius: '12px',
                bgcolor: 'rgba(0,0,0,0.6)',
              }}
            >
              <CircularProgress sx={{ color: 'white' }} size={32} />
              <Typography variant="body2" color="white">
                <T>Iniciando cámara...</T>
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '200px',
              height: '200px',
              border: '2px solid #ffffff',
              borderRadius: '8px',
              pointerEvents: 'none'
            }}
          />
        </Box>
      )}

      {invalidQrMessage && (
        <Alert severity="warning" sx={{ mt: 2, maxWidth: 400, width: '100%' }}>
          <T>Ese código QR no es válido para Notiline</T>
        </Alert>
      )}
    </Box>
  );
};

export default QRScanner;
